import React, { useState, useEffect, useRef, useCallback } from 'react';
import { auth, db } from '../firebase';
import openForConnectionsService, { OpenForConnection } from '../services/openForConnectionsService';
import { useAuthState } from 'react-firebase-hooks/auth';
import { addDoc, collection, doc, getDoc, updateDoc, onSnapshot, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { ensureConnectionJourneyFields } from '../services/connectionJourneyService';
import ConnectionAcceptedModal from './ConnectionAcceptedModal';
import IntroductionModal from './IntroductionModal';
import { useToast, ToastContainer } from './ui/Toast';
import { UserPlus, Handshake, MapPin } from 'phosphor-react';

interface OpenForConnectionsListProps {
  onConnect: (userId: string, userName: string, userPhoto?: string) => void;
  className?: string;
  searchName?: string;
  searchIndustry?: string;
  searchPostcode?: string;
  minimal?: boolean;
  settingsOnly?: boolean;
}

const NEDERLAND_REGIONS = [
  'Noord-Holland', 'Zuid-Holland', 'Utrecht', 'Flevoland',
  'Gelderland', 'Overijssel', 'Drenthe', 'Friesland',
  'Groningen', 'Noord-Brabant', 'Limburg', 'Zeeland'
];

const INTERNATIONAL_REGIONS = [
  'België - Vlaanderen', 'België - Wallonië', 'Duitsland',
  'Frankrijk', 'Verenigd Koninkrijk', 'Internationaal'
];

/**
 * Genereer een beknopte, menselijke match-reden op basis van beschikbare profieldata.
 * Geen percentages, geen technische labels — zakelijke relevantie in één zin.
 */
function generateMatchReason(
  currentUserProfile: any,
  openUser: OpenForConnection
): string {
  const normalize = (v: string[] | string | undefined): string[] => {
    if (!v) return [];
    if (Array.isArray(v)) return v.map(s => s.toLowerCase().trim());
    return [v.toLowerCase().trim()];
  };

  const myIndustry = normalize(currentUserProfile?.industry);
  const myTarget = normalize(currentUserProfile?.targetClientIndustry);
  const theirIndustry = normalize(openUser.userIndustry);
  const theirSectors = normalize(openUser.userSectors);
  const theirAll = [...new Set([...theirIndustry, ...theirSectors])];

  const aWantsB = myTarget.length > 0 && theirAll.some(i => myTarget.includes(i));
  const bWantsA = myIndustry.length > 0 && normalize(currentUserProfile?.targetClientIndustry ?? []).length === 0
    ? false
    : false; // Uitgebreid zodra openUser.targetClientIndustry beschikbaar is

  if (aWantsB) return 'Actief in de sector waar jij naar op zoek bent.';
  if (bWantsA) return 'Zoekt klanten in jouw branche.';
  return 'Net als jij bezig met het bouwen van eigen doelstellingen.';
}

const OpenForConnectionsList: React.FC<OpenForConnectionsListProps> = ({ 
  onConnect, 
  className = '',
  searchName = '',
  searchIndustry = '',
  searchPostcode = '',
  minimal = false,
  settingsOnly = false
}) => {
  const [user] = useAuthState(auth);
  const { toasts, success, error: showError, removeToast } = useToast();
  const [openUsers, setOpenUsers] = useState<OpenForConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [connectingUsers, setConnectingUsers] = useState<Set<string>>(new Set());
  const [pendingUserIds, setPendingUserIds] = useState<Set<string>>(new Set()); // ✅ FIX: Track users with pending requests (sent by current user)
  const [incomingRequestUserIds, setIncomingRequestUserIds] = useState<Set<string>>(new Set()); // Track users who sent requests to current user
  const [userRegions, setUserRegions] = useState<string[]>([]);
  const [showAcceptPopup, setShowAcceptPopup] = useState(false);
  const [acceptedUser, setAcceptedUser] = useState<{ userId: string; userName: string; userPhoto?: string; chatRoomId: string; connectionId?: string; isOtherOnline?: boolean } | null>(null);
  const [isOnboarded, setIsOnboarded] = useState<boolean>(false);
  const [showRegionSelector, setShowRegionSelector] = useState(false);
  const [expandedSection, setExpandedSection] = useState<'nederland' | 'internationaal' | null>('nederland');
  const [mobileExpandedRegion, setMobileExpandedRegion] = useState<'nederland' | 'internationaal' | null>(null);
  const [savingRegions, setSavingRegions] = useState(false);
  const [isOpenForConnections, setIsOpenForConnections] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [showIntroductionModal, setShowIntroductionModal] = useState(false);
  const [selectedUserForIntroduction, setSelectedUserForIntroduction] = useState<{userId: string, userName: string, userPhoto?: string} | null>(null);
  const [showRequestPreviewModal, setShowRequestPreviewModal] = useState(false);
  const [previewRequestData, setPreviewRequestData] = useState<any>(null);
  const [processingRequest, setProcessingRequest] = useState(false);
  const [incomingRequestDetails, setIncomingRequestDetails] = useState<Array<{
    requestId: string;
    fromUserId: string;
    fromUserName: string;
    fromUserPhoto?: string;
    fromUserCompany?: string;
    message?: string;
    knownContact?: any;
    createdAt?: Date;
  }>>([]);
  const incomingCountRef = React.useRef<number | null>(null);
  const hasInitializedSoundRef = React.useRef(false);
  const lastSoundTimestampRef = React.useRef<number>(0);

  // ✅ FIX: Use sessionStorage + ref to track known incoming IDs across re-renders/screenshots
  useEffect(() => {
    // Load known incoming IDs from sessionStorage
    try {
      const stored = sessionStorage.getItem('knownIncomingRequestIds');
      if (stored) {
        const knownIds = JSON.parse(stored) as string[];
        incomingCountRef.current = knownIds.length;
        console.log('🔔 Restored known incoming count from session:', knownIds.length);
      }
    } catch (e) {
      // ignore
    }
    
    const timer = setTimeout(() => {
      hasInitializedSoundRef.current = true;
      // Set baseline count AFTER initialization if not already set from sessionStorage
      if (incomingCountRef.current === null) {
        incomingCountRef.current = incomingRequestUserIds.size;
      }
      // Save current IDs to sessionStorage
      try {
        sessionStorage.setItem('knownIncomingRequestIds', JSON.stringify(Array.from(incomingRequestUserIds)));
      } catch (e) { /* ignore */ }
      console.log('🔔 Sound system initialized. Baseline incoming count:', incomingCountRef.current);
    }, 5000); // 5 second grace period
    return () => clearTimeout(timer);
  }, []); // Only run once on mount

  // 🔔 Play sound when a new connection request naar jou binnenkomt
  useEffect(() => {
    // Don't play sound during initialization phase
    if (!hasInitializedSoundRef.current) {
      console.log('🔔 Sound skipped: still initializing...');
      return;
    }

    const currentCount = incomingRequestUserIds.size;
    const previousCount = incomingCountRef.current;
    const now = Date.now();

    // Throttle: don't play sound more than once per 30 seconds
    const timeSinceLastSound = now - lastSoundTimestampRef.current;

    // Compare actual IDs, not just counts - to detect truly NEW requests
    let hasNewIds = false;
    try {
      const storedRaw = sessionStorage.getItem('knownIncomingRequestIds');
      const knownIds = storedRaw ? new Set(JSON.parse(storedRaw) as string[]) : new Set<string>();
      const currentIds = Array.from(incomingRequestUserIds);
      hasNewIds = currentIds.some(id => !knownIds.has(id));
      
      // Update sessionStorage with current IDs
      sessionStorage.setItem('knownIncomingRequestIds', JSON.stringify(currentIds));
    } catch (e) {
      // Fallback to count-based detection
      hasNewIds = previousCount !== null && currentCount > previousCount;
    }

    // Only play sound if there are truly NEW IDs and enough time has passed
    if (hasNewIds && timeSinceLastSound > 30000) {
      console.log('🔔 New connection request detected! Playing sound...', { previousCount, currentCount });
      lastSoundTimestampRef.current = now;
      try {
        const audio = new Audio('/sounds/connection-request.mp3');
        const playPromise = audio.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(err => {
            console.warn('Connection-request sound blocked by browser:', err);
          });
        }
      } catch (err) {
        console.warn('Kon connection-request geluid niet afspelen:', err);
      }
    }

    incomingCountRef.current = currentCount;
  }, [incomingRequestUserIds]);

  // 🔔 Play sound when a verbinding is geaccepteerd (match popup)
  useEffect(() => {
    if (showAcceptPopup && acceptedUser) {
      try {
        const audio = new Audio('/sounds/Openapp-Sound.mp3');
        const playPromise = audio.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(err => {
            console.warn('Connection-accepted sound blocked by browser:', err);
          });
        }
      } catch (err) {
        console.warn('Kon connection-accepted geluid niet afspelen:', err);
      }
    }
  }, [showAcceptPopup, acceptedUser]);

  // ✅ FIX: Real-time listener for userProfiles (bidirectional sync with ProfileSettings)
  useEffect(() => {
    if (!user?.uid) return;
    
    const userProfilesRef = doc(db, 'userProfiles', user.uid);
    
    // Real-time listener for userProfiles changes (from ProfileSettings)
    const unsubscribe = onSnapshot(userProfilesRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        
        // Only update regions if they actually changed (prevent unnecessary re-renders)
        if (data.selectedRegions) {
          setUserRegions(prev => {
            const newRegions = data.selectedRegions as string[];
            if (prev.length === newRegions.length && prev.every((r, i) => r === newRegions[i])) {
              return prev; // Same content, keep same reference
            }
            return newRegions;
          });
        }
        
        // Update other profile data
        setIsOnboarded(data.onboarding?.status === 'done' || data.isOnboarded === true);
        setUserProfile(data);
      }
    }, (error) => {
      console.error('❌ Error in OpenForConnectionsList real-time listener:', error);
    });
    
    return () => {
      unsubscribe();
    };
  }, [user?.uid]);
  
  // Load initial user profile data and check open status
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user?.uid) return;
      
      try {
        const userDoc = await getDoc(doc(db, 'userProfiles', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserRegions(data.selectedRegions || []);
          setIsOnboarded(data.onboarding?.status === 'done' || data.isOnboarded === true);
          setUserProfile(data);
        }

        // Check if user is currently open for connections
        const openQuery = await import('firebase/firestore').then(({ collection, query, where, getDocs }) =>
          getDocs(query(
            collection(db, 'openForConnections'),
            where('userId', '==', user.uid),
            where('isActive', '==', true)
          ))
        );
        setIsOpenForConnections(!openQuery.empty);
      } catch (error) {
        console.error('Error loading user profile:', error);
      }
    };

    loadUserProfile();
  }, [user?.uid]);

  // Ref so saveRegions can trigger full list reload (loadAllData is defined later)
  const loadAllDataRef = useRef<(bg: boolean) => Promise<void>>(async () => {});

  const saveRegions = async (newRegions: string[]) => {
    if (!user?.uid) return;
    setSavingRegions(true);
    try {
      const userRef = doc(db, 'userProfiles', user.uid);
      await updateDoc(userRef, {
        selectedRegions: newRegions,
        lastUpdated: new Date()
      });
      console.log('✅ Regio\'s opgeslagen:', newRegions);
      success('Regiovoorkeuren opgeslagen.');
      await loadAllDataRef.current(true);
    } catch (error) {
      console.error('❌ Fout bij opslaan regio\'s:', error);
      showError('Opslaan mislukt. Probeer het opnieuw.');
    } finally {
      setSavingRegions(false);
    }
  };

  const ensureConnectionId = async (otherUserId: string): Promise<string | null> => {
    if (!user?.uid) return null;

    const connectionsQuery1 = query(
      collection(db, 'connections'),
      where('user1Id', '==', user.uid),
      where('user2Id', '==', otherUserId)
    );
    const connectionsQuery2 = query(
      collection(db, 'connections'),
      where('user1Id', '==', otherUserId),
      where('user2Id', '==', user.uid)
    );

    const [connections1, connections2] = await Promise.all([
      getDocs(connectionsQuery1),
      getDocs(connectionsQuery2)
    ]);

    if (connections1.docs.length > 0) return connections1.docs[0].id;
    if (connections2.docs.length > 0) return connections2.docs[0].id;

    // Create minimal connection doc if none exists
    try {
      const connectionData = {
        user1Id: user.uid,
        user2Id: otherUserId,
        user1Name: userProfile?.displayName || user?.displayName || 'Jij',
        user2Name: acceptedUser?.userName || 'Connectie',
        user1Photo: userProfile?.photoURL || user?.photoURL || '',
        user2Photo: acceptedUser?.userPhoto || '',
        status: 'accepted',
        createdAt: new Date(),
        chatCount: 0,
        newForUserIds: [otherUserId]
      };
      const connectionRef = await addDoc(collection(db, 'connections'), connectionData);
      return connectionRef.id;
    } catch (err) {
      console.error('❌ Kon connectionId niet creëren voor agenda-sync:', err);
      return null;
    }
  };

  // Handle region toggle
  const handleRegionToggle = (region: string) => {
    const newRegions = userRegions.includes(region)
      ? userRegions.filter(r => r !== region)
      : [...userRegions, region];
    
    setUserRegions(newRegions);
    saveRegions(newRegions);
  };

  // Check if user has phone number
  const hasPhoneNumber = !!(userProfile?.phone || userProfile?.phoneNumber);

  // Toggle open/closed for connections
  const handleToggleOpenStatus = async () => {
    if (!user?.uid || !userProfile) return;
    
    // Check for phone number before opening
    if (!isOpenForConnections && !hasPhoneNumber) {
      alert('⚠️ Telefoonnummer vereist\n\nVul eerst je telefoonnummer in bij Instellingen → Profiel om zichtbaar te zijn voor anderen in "Open voor connecties".');
      return;
    }
    
    setTogglingStatus(true);
    try {
      if (isOpenForConnections) {
        // Close for connections
        await openForConnectionsService.closeForConnections(user.uid);
        setIsOpenForConnections(false);
        console.log('✅ Gesloten voor connecties');
        success('Status opgeslagen: je bent niet meer zichtbaar voor nieuwe connecties.');
      } else {
        // Open for connections
        await openForConnectionsService.setOpenForConnections(user.uid, {
          userName: userProfile.displayName || user.displayName || 'Onbekend',
          userEmail: userProfile.email || user.email || '',
          userPhoto: userProfile.photoURL || user.photoURL,
          photoPosition: userProfile.photoPosition,
          userRegion: userRegions[0] || 'Nederland',
          userRegions: userRegions,
          userCompany: userProfile.company || '',
          userBio: userProfile.bio || '',
          subscriptionTier: userProfile.subscriptionTier || 'free'
        });
        setIsOpenForConnections(true);
        console.log('✅ Open voor connecties');
        success('Status opgeslagen: je bent zichtbaar voor nieuwe connecties.');
        
        // ✅ FIX: Reload via loadAllData for consistent filtering (placeholders, connected users)
        await loadAllData(false);
      }
    } catch (error) {
      console.error('❌ Fout bij wijzigen status:', error);
      showError('Status wijzigen mislukt. Probeer het opnieuw.');
    } finally {
      setTogglingStatus(false);
    }
  };

  // ============================
  // REFS for background refresh debouncing
  // ============================
  const hasLoadedOnceRef = useRef(false);
  const backgroundRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isCancelledRef = useRef(false);

  // ============================
  // LOAD ALL DATA (single function, no race conditions)
  // ============================
  const loadAllData = useCallback(async (isBackgroundRefresh = false) => {
    if (!user?.uid || isCancelledRef.current) return;
    
    // Only show spinner on FIRST load (no data yet)
    if (!isBackgroundRefresh && !hasLoadedOnceRef.current) {
      setLoading(true);
    }
    
    try {
      const { collection, query, where, getDocs, doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      
      // 1️⃣ Get open users from service (now auto-cleans expired entries)
      const openUsersResult = await openForConnectionsService.getOpenForConnections(user.uid, 20);
      console.log('📋 Step 1: Open users from service:', openUsersResult.length, openUsersResult.map(u => u.userName));
      
      // 2️⃣ Get ALL connection requests involving current user
      const [sentPendingSnap, incomingSnap1, incomingSnap2] = await Promise.all([
        getDocs(query(
          collection(db, 'connectionRequests'),
          where('fromUserId', '==', user.uid),
          where('status', '==', 'pending')
        )),
        getDocs(query(
          collection(db, 'connectionRequests'),
          where('toUserId', '==', user.uid),
          where('status', '==', 'pending')
        )),
        getDocs(query(
          collection(db, 'connectionRequests'),
          where('receiverId', '==', user.uid),
          where('status', '==', 'pending')
        ))
      ]);
      
      const newPendingIds = new Set(
        sentPendingSnap.docs.map(d => d.data().toUserId || d.data().receiverId)
      );
      const newIncomingIds = new Set([
        ...incomingSnap1.docs.map(d => d.data().fromUserId || d.data().senderId),
        ...incomingSnap2.docs.map(d => d.data().fromUserId || d.data().senderId)
      ]);
      
      console.log('📋 Step 2: Pending sent:', Array.from(newPendingIds));
      console.log('📋 Step 2: Incoming from:', Array.from(newIncomingIds));
      
      // Note: Don't return early on isCancelledRef - let the load complete
      // The finally block will always clear loading state
      if (!isCancelledRef.current) {
        setPendingUserIds(newPendingIds);
        setIncomingRequestUserIds(newIncomingIds);
      }
      
      // 3️⃣ Get existing connections (to filter out)
      const [conn1, conn2] = await Promise.all([
        getDocs(query(collection(db, 'connections'), where('user1Id', '==', user.uid))),
        getDocs(query(collection(db, 'connections'), where('user2Id', '==', user.uid)))
      ]);
      const connectedUserIds = new Set([
        ...conn1.docs.map(d => d.data().user2Id),
        ...conn2.docs.map(d => d.data().user1Id)
      ]);
      
      // 4️⃣ Filter out already-connected users
      const filteredUsers = openUsersResult.filter(u => !connectedUserIds.has(u.userId));
      
      // 5️⃣ Add users with incoming requests who are NOT in the openForConnections list
      const extraUsers: OpenForConnection[] = [];
      for (const incomingUserId of Array.from(newIncomingIds)) {
        const alreadyInList = filteredUsers.some(u => u.userId === incomingUserId);
        const alreadyConnected = connectedUserIds.has(incomingUserId);
        
        if (!alreadyInList && !alreadyConnected) {
          try {
            const profileDoc = await getDoc(doc(db, 'userProfiles', incomingUserId));
            if (profileDoc.exists()) {
              const pd = profileDoc.data();
              extraUsers.push({
                userId: incomingUserId,
                userName: pd.displayName || pd.email?.split('@')[0] || 'Gebruiker',
                userEmail: pd.email || '',
                userPhoto: pd.photoURL,
                userRegion: pd.onboardingData?.region || 'Nederland',
                userRegions: pd.selectedRegions || [],
                userCompany: pd.company || '',
                userBio: pd.bio || '',
                subscriptionTier: pd.subscriptionTier || 'free',
                createdAt: new Date(),
                expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
                isActive: true
              });
              console.log(`➕ Added incoming request user: ${pd.displayName} (${incomingUserId})`);
            }
          } catch (err) {
            console.warn('Could not load profile for incoming request user:', incomingUserId);
          }
        }
      }
      
      const allUsers = [...filteredUsers, ...extraUsers];
      
      // 6️⃣ Enrich with profile data + filter out placeholder accounts
      const profilePromises = allUsers.map(u =>
        getDoc(doc(db, 'userProfiles', u.userId)).catch(() => null)
      );
      const profileDocs = await Promise.all(profilePromises);
      
      const finalUsers: OpenForConnection[] = [];
      allUsers.forEach((u, i) => {
        const pd = profileDocs[i]?.exists() ? profileDocs[i]!.data() : null;
        let displayName = (u.userName || '').trim();
        const isPlaceholder = !displayName || displayName === 'Onbekend' || displayName === 'Gebruiker' || displayName === 'Unknown';
        
        if (isPlaceholder) {
          // ✅ FIX: Try to recover a real name from profile data before skipping
          const fallbackName = pd?.displayName || pd?.name || (pd?.email ? pd.email.split('@')[0] : null) || (u.userEmail ? u.userEmail.split('@')[0] : null);
          if (fallbackName && fallbackName.trim() && fallbackName.trim() !== 'Onbekend' && fallbackName.trim() !== 'Gebruiker') {
            displayName = fallbackName.trim();
            console.log(`🔄 Recovered name for placeholder "${u.userName}" (${u.userId}): "${displayName}"`);
          } else {
            console.log(`❌ Skipping placeholder: "${u.userName}" (${u.userId}) - no recoverable name`);
            return;
          }
        }
        
        finalUsers.push({
          ...u,
          userName: displayName,
          userPostcode: pd?.postcode || pd?.postalCode || pd?.onboardingData?.postcode || u.userPostcode,
          userIndustry: pd?.industry || pd?.onboardingData?.industry || u.userIndustry,
          userSectors: pd?.sectors || pd?.idealClient?.sectors || u.userSectors
        });
      });
      
      console.log('📋 Final result:', {
        openFromService: openUsersResult.length,
        afterConnectionFilter: filteredUsers.length,
        extraFromIncoming: extraUsers.length,
        total: allUsers.length,
        final: finalUsers.length,
        names: finalUsers.map(u => u.userName),
        pendingIds: Array.from(newPendingIds),
        incomingIds: Array.from(newIncomingIds),
        connectedIds: Array.from(connectedUserIds)
      });
      
      if (!isCancelledRef.current) {
        setOpenUsers(finalUsers);
        hasLoadedOnceRef.current = true;
      }
    } catch (error) {
      console.error('❌ Error loading open users:', error);
    } finally {
      // ✅ FIX: Always clear loading state to prevent spinner getting stuck
      // Even if cancelled, the new effect will re-trigger loadAllData
      setLoading(false);
    }
  }, [user?.uid]);

  loadAllDataRef.current = loadAllData;

  // ============================
  // Debounced background refresh (max 1x per 10 sec)
  // ============================
  const debouncedBackgroundRefresh = useCallback(() => {
    if (backgroundRefreshTimerRef.current) return; // Already scheduled
    backgroundRefreshTimerRef.current = setTimeout(() => {
      backgroundRefreshTimerRef.current = null;
      loadAllData(true); // background = no spinner
    }, 10000); // 10 second debounce
  }, [loadAllData]);

  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    isCancelledRef.current = false;

    // Initial load (shows spinner only this time)
    loadAllData(false);
    
    // ============================
    // REAL-TIME LISTENERS (update badges instantly, debounce full reload)
    // ============================
    let unsubFns: (() => void)[] = [];
    
    const setupListeners = async () => {
      const { collection, query, where, onSnapshot } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      
      // Track sent/incoming separately to merge correctly
      let sentIds1 = new Set<string>();
      let sentIds2 = new Set<string>();
      let inIds1 = new Set<string>();
      let inIds2 = new Set<string>();
      let isFirstSnapshot = { sent1: true, sent2: true, in1: true, in2: true };
      
      // Sent requests (schema 1: fromUserId)
      unsubFns.push(onSnapshot(
        query(collection(db, 'connectionRequests'), where('fromUserId', '==', user.uid), where('status', '==', 'pending')),
        (snap) => {
          sentIds1 = new Set(snap.docs.map(d => d.data().toUserId || d.data().receiverId));
          setPendingUserIds(new Set([...Array.from(sentIds1), ...Array.from(sentIds2)]));
          if (!isFirstSnapshot.sent1) debouncedBackgroundRefresh();
          isFirstSnapshot.sent1 = false;
        }
      ));
      
      // Sent requests (schema 2: senderId)
      unsubFns.push(onSnapshot(
        query(collection(db, 'connectionRequests'), where('senderId', '==', user.uid), where('status', '==', 'pending')),
        (snap) => {
          sentIds2 = new Set(snap.docs.map(d => d.data().toUserId || d.data().receiverId));
          setPendingUserIds(new Set([...Array.from(sentIds1), ...Array.from(sentIds2)]));
          if (!isFirstSnapshot.sent2) debouncedBackgroundRefresh();
          isFirstSnapshot.sent2 = false;
        }
      ));
      
      // Incoming requests (schema 1: toUserId)
      unsubFns.push(onSnapshot(
        query(collection(db, 'connectionRequests'), where('toUserId', '==', user.uid), where('status', '==', 'pending')),
        (snap) => {
          inIds1 = new Set(snap.docs.map(d => d.data().fromUserId || d.data().senderId));
          setIncomingRequestUserIds(new Set([...Array.from(inIds1), ...Array.from(inIds2)]));
          if (!isFirstSnapshot.in1) debouncedBackgroundRefresh();
          isFirstSnapshot.in1 = false;
        }
      ));
      
      // Incoming requests (schema 2: receiverId)
      unsubFns.push(onSnapshot(
        query(collection(db, 'connectionRequests'), where('receiverId', '==', user.uid), where('status', '==', 'pending')),
        (snap) => {
          inIds2 = new Set(snap.docs.map(d => d.data().fromUserId || d.data().senderId));
          setIncomingRequestUserIds(new Set([...Array.from(inIds1), ...Array.from(inIds2)]));
          if (!isFirstSnapshot.in2) debouncedBackgroundRefresh();
          isFirstSnapshot.in2 = false;
        }
      ));

      // When anyone opens/closes for connections, refresh the list after short delay so new accounts appear quickly
      const openForConnectionsRef = collection(db, 'openForConnections');
      let openListRefreshTimer: ReturnType<typeof setTimeout> | null = null;
      unsubFns.push(onSnapshot(
        query(openForConnectionsRef, where('isActive', '==', true)),
        () => {
          if (openListRefreshTimer) clearTimeout(openListRefreshTimer);
          openListRefreshTimer = setTimeout(() => {
            openListRefreshTimer = null;
            loadAllData(true);
          }, 3000);
        }
      ));
      unsubFns.push(() => {
        if (openListRefreshTimer) clearTimeout(openListRefreshTimer);
      });
    };
    
    setupListeners();
    
    return () => {
      isCancelledRef.current = true;
      unsubFns.forEach(fn => fn());
      if (backgroundRefreshTimerRef.current) {
        clearTimeout(backgroundRefreshTimerRef.current);
        backgroundRefreshTimerRef.current = null;
      }
    };
  }, [user?.uid, loadAllData, debouncedBackgroundRefresh]);

  // ✅ Retries when list stays empty so new "open" accounts appear sooner (2s, 5s, 15s; max 3 retries)
  const retryCountRef = useRef(0);
  useEffect(() => {
    if (loading || openUsers.length > 0 || !user?.uid) return;
    if (!hasLoadedOnceRef.current) return;
    if (retryCountRef.current >= 3) return;

    const delays = [2000, 5000, 15000];
    const idx = retryCountRef.current;
    const delay = delays[idx];
    retryCountRef.current += 1;

    const timer = setTimeout(() => {
      console.log('🔄 Retry: list still empty, refreshing...', retryCountRef.current);
      loadAllData(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [loading, openUsers.length, user?.uid, loadAllData]);

  // ✅ Fetch details for incoming requests (names, photos, messages)
  useEffect(() => {
    if (!user?.uid || incomingRequestUserIds.size === 0) {
      setIncomingRequestDetails([]);
      return;
    }

    const fetchDetails = async () => {
      const { collection, query, where, getDocs, doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../firebase');

      const details: typeof incomingRequestDetails = [];

      for (const fromUserId of Array.from(incomingRequestUserIds)) {
        try {
          // Find the request document
          const [snap1, snap2] = await Promise.all([
            getDocs(query(
              collection(db, 'connectionRequests'),
              where('fromUserId', '==', fromUserId),
              where('toUserId', '==', user.uid),
              where('status', '==', 'pending')
            )).catch(() => ({ docs: [] as any[] })),
            getDocs(query(
              collection(db, 'connectionRequests'),
              where('senderId', '==', fromUserId),
              where('receiverId', '==', user.uid),
              where('status', '==', 'pending')
            )).catch(() => ({ docs: [] as any[] }))
          ]);

          const requestDoc = snap1.docs[0] || snap2.docs[0];
          const requestData = requestDoc?.data();

          // Get sender profile
          const profileDoc = await getDoc(doc(db, 'userProfiles', fromUserId));
          const profile = profileDoc.exists() ? profileDoc.data() : null;

          details.push({
            requestId: requestDoc?.id || '',
            fromUserId,
            fromUserName: profile?.displayName || requestData?.fromUserName || requestData?.senderName || 'Gebruiker',
            fromUserPhoto: profile?.photoURL || requestData?.fromUserPhoto,
            fromUserCompany: profile?.company || profile?.onboardingData?.company || '',
            message: requestData?.message || '',
            knownContact: requestData?.knownContact,
            createdAt: requestData?.createdAt?.toDate?.() || new Date()
          });
        } catch (err) {
          console.warn('Could not load request details for', fromUserId, err);
        }
      }

      setIncomingRequestDetails(details);
    };

    fetchDetails();
  }, [user?.uid, incomingRequestUserIds]);

  const handleAcceptIncomingRequest = async (fromUserId: string) => {
    if (!user?.uid) {
      alert('Je moet ingelogd zijn om een connectie te accepteren.');
      return;
    }

    if (connectingUsers.has(fromUserId)) {
      return; // Prevent double clicks
    }

    try {
      setConnectingUsers(prev => new Set(prev).add(fromUserId));
      
      // Navigate to dashboard to use the accept logic there
      // Or implement accept logic here similar to CommunityDashboard
      const { collection, query, where, getDocs, doc, updateDoc, addDoc, serverTimestamp, setDoc } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      
      // ✅ Find ALL pending requests between these two users (BOTH directions, BOTH schema variants)
      const [snap1, snap2, snap3, snap4] = await Promise.all([
        // Direction 1: fromUserId → me (toUserId)
        getDocs(query(
          collection(db, 'connectionRequests'),
          where('fromUserId', '==', fromUserId),
          where('toUserId', '==', user.uid),
          where('status', '==', 'pending')
        )).catch(() => ({ docs: [] as any[] })),
        // Direction 1: senderId → me (receiverId)
        getDocs(query(
          collection(db, 'connectionRequests'),
          where('senderId', '==', fromUserId),
          where('receiverId', '==', user.uid),
          where('status', '==', 'pending')
        )).catch(() => ({ docs: [] as any[] })),
        // ✅ Direction 2 (reverse): me → fromUserId (auto-accept these too!)
        getDocs(query(
          collection(db, 'connectionRequests'),
          where('fromUserId', '==', user.uid),
          where('toUserId', '==', fromUserId),
          where('status', '==', 'pending')
        )).catch(() => ({ docs: [] as any[] })),
        // Direction 2 (reverse alt schema)
        getDocs(query(
          collection(db, 'connectionRequests'),
          where('senderId', '==', user.uid),
          where('receiverId', '==', fromUserId),
          where('status', '==', 'pending')
        )).catch(() => ({ docs: [] as any[] }))
      ]);
      
      const allPendingRequests = [...snap1.docs, ...snap2.docs, ...snap3.docs, ...snap4.docs];
      
      if (allPendingRequests.length === 0) {
        alert('Geen pending request gevonden.');
        setConnectingUsers(prev => {
          const next = new Set(prev);
          next.delete(fromUserId);
          return next;
        });
        return;
      }
      
      // Get OTHER person's profile
      const otherProfileDoc = await getDoc(doc(db, 'userProfiles', fromUserId));
      const otherProfile = otherProfileDoc.exists() ? otherProfileDoc.data() : null;
      const senderName = otherProfile?.displayName || otherProfile?.email?.split('@')[0] || 'Gebruiker';
      const senderPhoto = otherProfile?.photoURL;
      const senderOnline = otherProfile?.isOnline === true;
      
      // Get MY profile name
      const myName = userProfile?.displayName || user?.displayName || 'Gebruiker';
      
      // ✅ Accept ALL pending requests (both directions) at once
      const updatePromises = allPendingRequests.map(reqDoc => 
        updateDoc(doc(db, 'connectionRequests', reqDoc.id), {
          status: 'accepted',
          acceptedAt: serverTimestamp()
        })
      );
      
      await Promise.all(updatePromises);
      console.log(`✅ ${allPendingRequests.length} connection request(s) accepted (both directions)`);
      
      // ✅ FIX: Create connection document + mark as 'nieuw' voor de verzender
      const connectionsQuery1 = query(
        collection(db, 'connections'),
        where('user1Id', '==', user.uid),
        where('user2Id', '==', fromUserId)
      );
      const connectionsQuery2 = query(
        collection(db, 'connections'),
        where('user1Id', '==', fromUserId),
        where('user2Id', '==', user.uid)
      );
      const [connections1, connections2] = await Promise.all([
        getDocs(connectionsQuery1),
        getDocs(connectionsQuery2)
      ]);
      
      let connectionId: string | null = null;
      if (connections1.docs.length > 0) {
        connectionId = connections1.docs[0].id;
        console.log('✅ Existing connection found:', connectionId);
      } else if (connections2.docs.length > 0) {
        connectionId = connections2.docs[0].id;
        console.log('✅ Existing connection found:', connectionId);
      } else {
        // Create new connection
        const connectionData = {
          user1Id: user.uid,          // ontvanger van het verzoek
          user2Id: fromUserId,        // verzender van het verzoek
          user1Name: userProfile?.displayName || user?.displayName || 'Jij',
          user2Name: senderName,
          user1Photo: userProfile?.photoURL || user?.photoURL || '',
          user2Photo: senderPhoto || '',
          status: 'accepted',
          createdAt: new Date(),
          chatCount: 0,
          // Markeer dat deze connectie nieuw is voor de verzender (zodat hij een melding kan krijgen)
          newForUserIds: [fromUserId]
        };
        const connectionRef = await addDoc(collection(db, 'connections'), connectionData);
        connectionId = connectionRef.id;
        console.log('✅ Connection created:', connectionId);
      }
      
      // Find or create chat room
      const chatRoomsQuery = query(
        collection(db, 'chatRooms'),
        where('participants', 'array-contains', user.uid)
      );
      const chatRoomsSnapshot = await getDocs(chatRoomsQuery);
      let chatRoom = chatRoomsSnapshot.docs.find(doc => {
        const data = doc.data();
        return data.participants.includes(fromUserId) && data.participants.length === 2;
      });
      
      if (!chatRoom) {
        // Create new chat room
        const newRoomData = {
          participants: [user.uid, fromUserId],
          participantNames: [userProfile?.displayName || user?.displayName || 'Jij', senderName],
          participantPhotos: [userProfile?.photoURL || user?.photoURL || '', senderPhoto || ''],
          messageCount: 0,
          unreadCount: { [user.uid]: 0, [fromUserId]: 0 },
          createdAt: new Date(),
          callTriggerData: {
            messagesSinceLastTrigger: 0,
            triggerShown: false
          },
          connectionId: connectionId || undefined
        };
        
        const chatRoomRef = await addDoc(collection(db, 'chatRooms'), newRoomData);
        chatRoom = { id: chatRoomRef.id, data: () => newRoomData } as any;
        console.log('✅ Chat room created:', chatRoomRef.id);
        
        // ✅ FIX: Send welcome message as SYSTEM message with correct names
        const { addDoc: addDocMsg } = await import('firebase/firestore');
        const welcomeMessage = {
          chatRoomId: chatRoomRef.id,
          senderId: 'system',
          senderName: 'SuperKonnected',
          senderPhoto: '',
          message: `🎉 ${myName} en ${senderName} zijn nu verbonden! Start het gesprek.`,
          timestamp: new Date(),
          messageType: 'system'
        };
        await addDocMsg(collection(db, 'chatMessages'), welcomeMessage);
        console.log('✅ System welcome message sent');
      } else {
        // Update existing chat room with connectionId if not set
        if (connectionId && !chatRoom.data().connectionId) {
          await updateDoc(doc(db, 'chatRooms', chatRoom.id), {
            connectionId: connectionId
          });
        }
      }
      
      // Create or ensure connection flow document exists voor ConnectieFlow
      // Let op: we laten ConnectieFlow zelf de timer/fase starten op basis van chat-activiteit
      if (chatRoom) {
        const flowRef = doc(db, 'connectionFlows', chatRoom.id);
        const existingFlowSnap = await getDoc(flowRef);

        if (!existingFlowSnap.exists()) {
          // Structuur alignen met ConnectieFlow.findOrCreateConnectionFlow
          const participants = [user.uid, fromUserId];
          const flowData = {
            chatRoomId: chatRoom.id,
            participants,
            participantNames: [userProfile?.displayName || user?.displayName || 'Jij', senderName],
            status: 'pending',
            // ❌ REMOVED: currentStep (legacy field, not used anymore)
            chatStep: {
              status: 'pending',
              acceptances: {},
              duration: 480 // 8 minuten
            },
            callStep: {
              status: 'pending',
              acceptances: {},
              duration: 1800
            },
            videoStep: {
              status: 'pending',
              acceptances: {},
              duration: 1800
            },
            physicalStep: {
              status: 'pending',
              acceptances: {}
            },
            createdAt: new Date(),
            updatedAt: new Date()
          };

          await setDoc(flowRef, flowData as any);
          console.log('✅ Connection flow seed created for room:', chatRoom.id);
        } else {
          console.log('ℹ️ Connection flow already exists for room:', chatRoom.id);
        }
      } else {
        console.error('❌ Cannot ensure connection flow: chatRoom is undefined');
      }
      
      // ✅ FIX: Show popup instead of direct navigation
      if (chatRoom) {
        console.log('🔍 DEBUG: Setting acceptedUser with connectionId:', connectionId);
        console.log('🔍 DEBUG: chatRoom.id:', chatRoom.id);
        console.log('🔍 DEBUG: fromUserId:', fromUserId);
        
        setAcceptedUser({
          userId: fromUserId,
          userName: senderName,
          userPhoto: senderPhoto,
          chatRoomId: chatRoom.id,
          connectionId: connectionId || undefined,
          isOtherOnline: senderOnline
        });
        
        if (!connectionId) {
          console.error('❌ WARNING: connectionId is null/undefined! Modal may not show!');
        }
        
        await ensureConnectionJourneyFields(connectionId);
        setShowAcceptPopup(true);
        console.log('✅ showAcceptPopup set to true, acceptedUser:', {
          userId: fromUserId,
          userName: senderName,
          connectionId: connectionId || undefined
        });
      } else {
        console.error('Chat room not found after creation');
        alert('Er ging iets mis bij het aanmaken van de chat room.');
      }
      
      // Reload the list
      const users = await openForConnectionsService.getOpenForConnections(user.uid, 20);
      setOpenUsers(users);
      
    } catch (error) {
      console.error('Error accepting connection request:', error);
      alert('Er ging iets mis bij het accepteren van het verzoek.');
    } finally {
      setConnectingUsers(prev => {
        const next = new Set(prev);
        next.delete(fromUserId);
        return next;
      });
    }
  };

  const handleConnect = async (openUser: OpenForConnection) => {
    if (!user?.uid) {
      alert('Je moet ingelogd zijn om een connectie te maken.');
      return;
    }

    if (connectingUsers.has(openUser.userId)) {
      return; // Prevent double clicks
    }

    // ✅ FIX: Don't send request here - let parent handle it via modal with "ik ken deze persoon al" checkbox
    // Just call the parent callback to open the modal
    onConnect(openUser.userId, openUser.userName, openUser.userPhoto);
  };

  const getTierBadge = (tier: string) => {
    switch (tier) {
      case 'free':
        return <span className="px-0.5 py-0 text-[8px] bg-gray-100 text-gray-600 rounded-full leading-tight">Free</span>;
      case 'premium':
        return <span className="px-0.5 py-0 text-[8px] bg-blue-100 text-blue-600 rounded-full leading-tight">Premium</span>;
      case 'enterprise':
        return <span className="px-0.5 py-0 text-[8px] bg-[rgba(46,95,86,0.1)] text-[#2E5F56] rounded-full leading-tight">Enterprise</span>;
      default:
        return <span className="px-0.5 py-0 text-[8px] bg-gray-100 text-gray-600 rounded-full leading-tight">Free</span>;
    }
  };

  if (loading && openUsers.length === 0) {
    return (
      <div className={`flex items-center justify-center py-6 sm:py-8 ${className}`}>
        <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-[#3ECBB5]"></div>
        <span className="ml-2 text-xs sm:text-sm text-gray-600">Laden...</span>
      </div>
    );
  }

  // Check if user needs to complete onboarding
  if (!isOnboarded) {
    return (
      <div className={`${className}`}>
        <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 shadow-sm">
          <div className="flex items-start space-x-2 sm:space-x-3">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs sm:text-sm font-semibold text-gray-700 flex-shrink-0">
              i
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-1 leading-tight">
                Voltooi je profiel
              </h3>
              <p className="text-[11px] sm:text-xs text-gray-700 mb-2 sm:mb-3 leading-snug">
                Om connecties te kunnen zien en maken, moet je eerst je onboarding voltooien.
              </p>
              <button
                onClick={() => {
                  window.location.hash = '#onboarding';
                }}
                className="inline-flex items-center px-3 sm:px-4 py-2 rounded-md border border-gray-300 text-[11px] sm:text-xs font-medium text-gray-800 hover:bg-gray-50 min-h-[44px]"
              >
                Naar onboarding
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render region selector component - Compact voor iPhone 8
  const RegionSelector = () => (
    <div className="bg-white border-b border-gray-200 mb-1 md:rounded-lg md:border md:shadow-sm">
      <button
        onClick={() => setShowRegionSelector(!showRegionSelector)}
        className="w-full py-0.5 px-1.5 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-1 min-w-0 flex-1">
          <h3 className="text-[10px] font-semibold text-gray-900 leading-tight">
            Jouw werkgebied
          </h3>
          <p className="text-[9px] text-gray-600 leading-tight truncate">
            {userRegions.length === 0 
              ? 'Geen regio'
              : `${userRegions.length} ${userRegions.length === 1 ? 'regio' : 'regio\'s'}`
            }
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {savingRegions && (
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-[#3ECBB5]"></div>
          )}
          <span className="text-gray-400 text-xs">
            {showRegionSelector ? '▼' : '▶'}
          </span>
        </div>
      </button>

      {showRegionSelector && (
        <div className="border-t border-gray-200 p-1 bg-gray-50">
          {/* Nederland Section */}
          <div className="mb-1">
            <button
              onClick={() => setExpandedSection(expandedSection === 'nederland' ? null : 'nederland')}
              className="w-full flex items-center justify-between py-0.5 px-1 bg-white rounded border border-gray-200 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-center gap-0.5">
                <span className="text-[10px] font-medium text-gray-700">Nederland</span>
                <span className="text-[9px] text-gray-500">
                  ({userRegions.filter(r => NEDERLAND_REGIONS.includes(r)).length}/{NEDERLAND_REGIONS.length})
                </span>
              </div>
              <span className="text-gray-400 text-[10px]">
                {expandedSection === 'nederland' ? '▼' : '▶'}
              </span>
            </button>

            {expandedSection === 'nederland' && (
              <div className="mt-0.5 grid grid-cols-2 gap-0.5 px-0.5">
                {NEDERLAND_REGIONS.map(region => (
                  <label
                    key={region}
                    className={`flex items-center gap-0.5 px-1 py-0.5 rounded cursor-pointer transition-all ${
                      userRegions.includes(region)
                        ? 'bg-blue-100 border border-blue-400'
                        : 'bg-white border border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={userRegions.includes(region)}
                      onChange={() => handleRegionToggle(region)}
                      className="form-checkbox h-2.5 w-2.5 text-blue-600 rounded flex-shrink-0"
                    />
                    <span className="text-[9px] text-gray-700 leading-tight truncate">{region}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* International Section */}
          <div>
            <button
              onClick={() => setExpandedSection(expandedSection === 'internationaal' ? null : 'internationaal')}
              className="w-full flex items-center justify-between py-0.5 px-1 bg-white rounded border border-gray-200 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-center gap-0.5">
                <span className="text-[10px] font-medium text-gray-700">Internationaal</span>
                <span className="text-[9px] text-gray-500">
                  ({userRegions.filter(r => INTERNATIONAL_REGIONS.includes(r)).length}/{INTERNATIONAL_REGIONS.length})
                </span>
              </div>
              <span className="text-gray-400 text-[10px]">
                {expandedSection === 'internationaal' ? '▼' : '▶'}
              </span>
            </button>

            {expandedSection === 'internationaal' && (
              <div className="mt-0.5 grid grid-cols-2 gap-0.5 px-0.5">
                {INTERNATIONAL_REGIONS.map(region => (
                  <label
                    key={region}
                    className={`flex items-center gap-0.5 px-1 py-0.5 rounded cursor-pointer transition-all ${
                      userRegions.includes(region)
                        ? 'bg-blue-100 border border-blue-400'
                        : 'bg-white border border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={userRegions.includes(region)}
                      onChange={() => handleRegionToggle(region)}
                      className="form-checkbox h-2.5 w-2.5 text-blue-600 rounded flex-shrink-0"
                    />
                    <span className="text-[9px] text-gray-700 leading-tight truncate">{region}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="mt-1 pt-1 border-t border-gray-200">
            <p className="text-[8px] text-gray-600 flex items-center gap-0.5 leading-tight">
              <span>✓</span>
              <span>Wijzigingen worden automatisch opgeslagen</span>
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const CompactRegionSelector = () => (
    <div className="rounded-2xl border border-[#E8E4DC] bg-white/90 shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setMobileExpandedRegion(mobileExpandedRegion === 'nederland' ? null : 'nederland')}
        className="flex w-full items-center justify-between px-3.5 py-3 text-left transition-colors active:bg-[#F4F1EA]"
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        <div className="min-w-0">
          <div className="text-[13px] font-semibold leading-tight text-[#2F3A36]">Nederland</div>
          <div className="mt-0.5 text-[11px] leading-tight text-[#8A8A84]">
            {userRegions.filter(r => NEDERLAND_REGIONS.includes(r)).length}/{NEDERLAND_REGIONS.length} regio's geselecteerd
          </div>
        </div>
        <span className="ml-3 flex-shrink-0 text-[15px] text-[#8A8A84]">
          {mobileExpandedRegion === 'nederland' ? 'v' : '>'}
        </span>
      </button>

      {mobileExpandedRegion === 'nederland' && (
        <div className="grid grid-cols-1 gap-2 border-t border-[#E8E4DC] bg-[#FBFAF7] px-3.5 py-3">
          {NEDERLAND_REGIONS.map(region => (
            <label
              key={region}
              className={`flex min-h-[38px] items-center gap-2 rounded-xl border px-3 text-[13px] transition-colors ${
                userRegions.includes(region)
                  ? 'border-[#8DA8A0] bg-[#F0F7F4] text-[#2F3A36]'
                  : 'border-[#E8E4DC] bg-white text-[#5E6462]'
              }`}
            >
              <input
                type="checkbox"
                checked={userRegions.includes(region)}
                onChange={() => handleRegionToggle(region)}
                className="h-4 w-4 flex-shrink-0 rounded border-gray-300 text-[#4F6660] focus:ring-[#4F6660]"
              />
              <span className="whitespace-nowrap leading-tight">{region}</span>
            </label>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => setMobileExpandedRegion(mobileExpandedRegion === 'internationaal' ? null : 'internationaal')}
        className="flex w-full items-center justify-between border-t border-[#E8E4DC] px-3.5 py-3 text-left transition-colors active:bg-[#F4F1EA]"
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        <div className="min-w-0">
          <div className="text-[13px] font-semibold leading-tight text-[#2F3A36]">Internationaal</div>
          <div className="mt-0.5 text-[11px] leading-tight text-[#8A8A84]">
            {userRegions.filter(r => INTERNATIONAL_REGIONS.includes(r)).length}/{INTERNATIONAL_REGIONS.length} regio's geselecteerd
          </div>
        </div>
        <span className="ml-3 flex-shrink-0 text-[15px] text-[#8A8A84]">
          {mobileExpandedRegion === 'internationaal' ? 'v' : '>'}
        </span>
      </button>

      {mobileExpandedRegion === 'internationaal' && (
        <div className="grid grid-cols-1 gap-2 border-t border-[#E8E4DC] bg-[#FBFAF7] px-3.5 py-3">
          {INTERNATIONAL_REGIONS.map(region => (
            <label
              key={region}
              className={`flex min-h-[38px] items-center gap-2 rounded-xl border px-3 text-[13px] transition-colors ${
                userRegions.includes(region)
                  ? 'border-[#8DA8A0] bg-[#F0F7F4] text-[#2F3A36]'
                  : 'border-[#E8E4DC] bg-white text-[#5E6462]'
              }`}
            >
              <input
                type="checkbox"
                checked={userRegions.includes(region)}
                onChange={() => handleRegionToggle(region)}
                className="h-4 w-4 flex-shrink-0 rounded border-gray-300 text-[#4F6660] focus:ring-[#4F6660]"
              />
              <span className="whitespace-nowrap leading-tight">{region}</span>
            </label>
          ))}
        </div>
      )}

      <div className="border-t border-[#E8E4DC] px-3.5 py-2">
        <p className="text-[11px] leading-tight text-[#8A8A84]">
          Wijzigingen worden automatisch opgeslagen{savingRegions ? '...' : '.'}
        </p>
      </div>
    </div>
  );

  const normalizedName = searchName.trim().toLowerCase();
  const normalizedIndustry = searchIndustry.trim().toLowerCase();
  const normalizedPostcode = searchPostcode.trim().toLowerCase().replace(/\s+/g, '');

  const filteredOpenUsers = openUsers.filter((openUser) => {
    const nameMatch = !normalizedName || openUser.userName.toLowerCase().includes(normalizedName);

    const industryParts = [
      openUser.userCompany,
      openUser.userBio,
      Array.isArray(openUser.userIndustry) ? openUser.userIndustry.join(' ') : openUser.userIndustry,
      Array.isArray(openUser.userSectors) ? openUser.userSectors.join(' ') : ''
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    const industryMatch = !normalizedIndustry || industryParts.includes(normalizedIndustry);

    const userPostcode = (openUser.userPostcode || '').toLowerCase().replace(/\s+/g, '');
    const postcodeMatch = !normalizedPostcode || userPostcode.includes(normalizedPostcode);

    return nameMatch && industryMatch && postcodeMatch;
  });

  return (
    <div className={`space-y-1.5 sm:space-y-2 md:space-y-4 ${className}`}>
      <ToastContainer toasts={toasts} onClose={removeToast} />
      {/* Netwerk status — compact als open, uitnodigend als gesloten */}
      {!minimal && (isOpenForConnections ? (
        <div className="bg-white border-b py-1 px-1.5 mb-1" style={{ borderColor: '#E9E7E2' }}>
          <div className="flex items-center justify-between gap-1">
            <div className="flex items-center gap-1 min-w-0">
              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#8DA8A0' }} />
              <span className="text-[10px] font-medium leading-tight" style={{ color: '#4F6660' }}>Zichtbaar voor netwerken</span>
            </div>
            <button
              onClick={handleToggleOpenStatus}
              disabled={togglingStatus}
              className="px-1.5 py-0.5 rounded text-[9px] font-medium transition-colors flex-shrink-0"
              style={{ backgroundColor: '#F4F4F1', color: '#5E6462' }}
            >
              {togglingStatus ? '...' : 'Verbergen'}
            </button>
          </div>
        </div>
      ) : (
        <div className="rounded-lg p-2.5 mb-2 border" style={{ backgroundColor: 'rgba(159,184,174,0.08)', borderColor: 'rgba(159,184,174,0.3)' }}>
          <div className="flex items-start gap-2">
            <UserPlus size={16} weight="regular" style={{ color: '#7A9488', flexShrink: 0, marginTop: 1 }} />
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold leading-snug mb-0.5" style={{ color: '#3C4F49' }}>
                Zet jezelf op de kaart
              </p>
              <p className="text-[10px] leading-snug mb-2" style={{ color: '#5E6462' }}>
                {openUsers.length > 0
                  ? `Er zijn ${openUsers.length} ondernemer${openUsers.length === 1 ? '' : 's'} actief in jouw regio. Maak jezelf zichtbaar.`
                  : 'Laat anderen in jouw regio weten dat je open staat voor kennismaking.'}
              </p>
              <button
                onClick={handleToggleOpenStatus}
                disabled={togglingStatus || userRegions.length === 0 || !hasPhoneNumber}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#8DA8A0', color: '#ffffff' }}
              >
                {togglingStatus ? 'Even wachten...' : 'Open voor netwerken'}
              </button>
              {(!hasPhoneNumber || userRegions.length === 0) && (
                <p className="text-[9px] mt-1 leading-snug" style={{ color: '#8DA8A0' }}>
                  {!hasPhoneNumber ? 'Vul eerst je telefoonnummer in bij Instellingen.' : 'Selecteer eerst een regio.'}
                </p>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* ✅ Inkomende connectieverzoeken - direct accepteren/weigeren */}
      {!minimal && !settingsOnly && incomingRequestDetails.length > 0 && (
        <div className="space-y-2 mb-2">
          <p className="text-[11px] font-bold text-green-800 flex items-center gap-1.5">
            <span>🔔</span>
            {incomingRequestDetails.length === 1 
              ? '1 nieuw connectieverzoek' 
              : `${incomingRequestDetails.length} nieuwe connectieverzoeken`}
          </p>
          {incomingRequestDetails.map((req) => (
            <div key={req.fromUserId} className="bg-green-50 border border-green-300 rounded-lg p-3 transition-all">
              <div className="flex items-center gap-3">
                {/* Avatar */}
                {req.fromUserPhoto ? (
                  <img 
                    src={req.fromUserPhoto} 
                    alt={req.fromUserName} 
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                    style={{
                      objectPosition: (req as any).photoPosition 
                        ? `${(req as any).photoPosition.x}% ${(req as any).photoPosition.y}%` 
                        : 'center'
                    }}
                    onError={(e) => {
                      const initials = (req.fromUserName || 'U').split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
                      (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=4F6660&color=fff&size=80`;
                    }}
                  />
                ) : (
                  <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-semibold text-sm">
                      {req.fromUserName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <h4 className="font-semibold text-gray-900 text-sm truncate">{req.fromUserName}</h4>
                    {req.knownContact && (
                      <span className="px-1.5 py-0.5 text-[9px] font-medium bg-[rgba(46,95,86,0.08)] text-[#4F6660] rounded-full whitespace-nowrap">👋 Bekende</span>
                    )}
                  </div>
                  {req.fromUserCompany && (
                    <p className="text-[10px] text-gray-600 truncate">{req.fromUserCompany}</p>
                  )}
                  {req.message && (
                    <p className="text-[10px] text-gray-700 mt-0.5 line-clamp-2 italic">"{req.message}"</p>
                  )}
                </div>
                
                {/* Buttons */}
                <div className="flex gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => {
                      handleAcceptIncomingRequest(req.fromUserId);
                    }}
                    disabled={connectingUsers.has(req.fromUserId)}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 min-h-[36px]"
                  >
                    {connectingUsers.has(req.fromUserId) ? '...' : '✓ Accepteren'}
                  </button>
                  <button
                    onClick={async () => {
                      if (!req.requestId) return;
                      try {
                        const { updateDoc, doc } = await import('firebase/firestore');
                        const { db } = await import('../firebase');
                        await updateDoc(doc(db, 'connectionRequests', req.requestId), {
                          status: 'declined',
                          updatedAt: new Date()
                        });
                        // Remove from details list
                        setIncomingRequestDetails(prev => prev.filter(r => r.fromUserId !== req.fromUserId));
                        setIncomingRequestUserIds(prev => {
                          const next = new Set(prev);
                          next.delete(req.fromUserId);
                          return next;
                        });
                      } catch (error) {
                        console.error('Error declining:', error);
                        alert('Er ging iets mis.');
                      }
                    }}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors min-h-[36px]"
                  >
                    ✗
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Fallback: als details nog laden maar IDs al bekend zijn */}
      {!minimal && !settingsOnly && incomingRequestUserIds.size > 0 && incomingRequestDetails.length === 0 && (
        <div className="bg-green-50 border border-green-300 rounded-lg p-2.5 mb-2 animate-pulse">
          <div className="flex items-center gap-2">
            <span className="text-green-600 text-lg flex-shrink-0">🔔</span>
            <p className="text-[12px] font-bold text-green-900">
              {incomingRequestUserIds.size === 1 
                ? '1 nieuw connectieverzoek laden...' 
                : `${incomingRequestUserIds.size} nieuwe connectieverzoeken laden...`}
            </p>
          </div>
        </div>
      )}

      {/* Phone Number Warning */}
      {!minimal && !hasPhoneNumber && (
        <div className="bg-[rgba(46,95,86,0.08)] border border-[#E0DDD8] rounded-lg p-2 sm:p-2.5 md:p-3 mb-1.5 sm:mb-2 md:mb-4">
          <div className="flex items-start gap-2">
            <span className="text-[#4F6660] text-sm flex-shrink-0">⚠️</span>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] sm:text-xs font-semibold text-[#4F6660] leading-snug mb-1">
                Telefoonnummer vereist
              </p>
              <p className="text-[10px] sm:text-xs text-[#4F6660] leading-snug">
                Anderen kunnen je niet zien in "Open voor connecties" zonder telefoonnummer. 
                Ga naar <strong>Instellingen → Profiel</strong> om je telefoonnummer toe te voegen.
              </p>
            </div>
          </div>
        </div>
      )}

      {!minimal && userRegions.length === 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 sm:p-2.5 md:p-3 mb-1.5 sm:mb-2 md:mb-4">
          <p className="text-[11px] sm:text-xs text-gray-700 leading-snug">
            Selecteer eerst één of meer regio's om te netwerken.
          </p>
        </div>
      )}

      {/* Always show region selector */}
      {!minimal && (settingsOnly ? <CompactRegionSelector /> : <RegionSelector />)}

      {/* Show users if regions are selected - Compact voor iPhone 8 */}
      {!settingsOnly && (minimal ? filteredOpenUsers.length > 0 : userRegions.length > 0 && filteredOpenUsers.length > 0) && (
        <>
      <div className={minimal ? "divide-y divide-gray-100" : "space-y-0 bg-white rounded-lg border border-gray-200 overflow-hidden"}>
        {filteredOpenUsers.map((openUser) => (
          <div key={openUser.userId} className={minimal ? "py-3 transition-colors" : "border-b border-gray-200 last:border-b-0 py-0.5 px-1.5 hover:bg-gray-50 transition-colors"}>
            <div className={minimal ? "flex items-center gap-3" : "flex items-center gap-1.5"}>
              {openUser.userPhoto ? (
                <img 
                  src={openUser.userPhoto} 
                  alt={openUser.userName} 
                  className={minimal ? "w-11 h-11 rounded-full object-cover flex-shrink-0" : "w-8 h-8 rounded-full object-cover flex-shrink-0"}
                  style={{
                    objectPosition: openUser.photoPosition 
                      ? `${openUser.photoPosition.x}% ${openUser.photoPosition.y}%` 
                      : 'center'
                  }}
                  onError={(e) => {
                    const initials = (openUser.userName || 'U').split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
                    (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=4F6660&color=fff&size=64`;
                  }}
                />
              ) : (
                <div className={minimal ? "w-11 h-11 bg-[#8DA8A0] rounded-full flex items-center justify-center flex-shrink-0" : "w-8 h-8 bg-[#2E5F56] rounded-full flex items-center justify-center flex-shrink-0"}>
                  <span className="text-white font-semibold text-xs">
                    {openUser.userName.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-0.5">
                  <div className="flex items-center gap-0.5 min-w-0 flex-1">
                    <h4 className={minimal ? "font-semibold text-gray-900 text-sm leading-tight truncate" : "font-medium text-gray-900 text-[11px] leading-tight truncate"}>{openUser.userName}</h4>
                    {getTierBadge(openUser.subscriptionTier)}
                  </div>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    {incomingRequestUserIds.has(openUser.userId) && (
                      <span className="px-1 py-0 bg-[rgba(46,95,86,0.08)] text-[#4F6660] text-[8px] font-medium rounded-full leading-tight whitespace-nowrap">
                        Wacht op jou
                      </span>
                    )}
                    {pendingUserIds.has(openUser.userId) && !incomingRequestUserIds.has(openUser.userId) && (
                      <span className="px-1 py-0 bg-[rgba(46,95,86,0.1)] text-[#2E5F56] text-[8px] font-medium rounded-full leading-tight whitespace-nowrap">
                        Verzonden
                      </span>
                    )}
                    <span className="text-[8px] text-gray-500 leading-tight whitespace-nowrap">
                      {openUser.createdAt instanceof Date 
                        ? openUser.createdAt.toLocaleDateString('nl-NL', { month: 'short', day: 'numeric' })
                        : new Date(openUser.createdAt).toLocaleDateString('nl-NL', { month: 'short', day: 'numeric' })
                      }
                    </span>
                  </div>
                </div>
                
                {(openUser.userCompany || openUser.userRegion) && (
                  <p className={minimal ? "text-[11px] text-gray-500 truncate mt-0.5 leading-tight" : "text-[8px] text-gray-600 truncate mt-0 leading-tight"}>
                    {openUser.userCompany && `${openUser.userCompany}${openUser.userRegion ? ' • ' : ''}`}
                    {openUser.userRegion && (
                      <span className="inline-flex items-center gap-0.5" style={{ color: '#7A9488' }}>
                        <MapPin size={7} weight="fill" />
                        {openUser.userRegion}
                      </span>
                    )}
                  </p>
                )}
                {/* Match reden — zakelijke relevantie in één zin */}
                <p className={minimal ? "text-[10px] mt-1 leading-snug" : "text-[8px] mt-0.5 leading-tight italic"} style={{ color: '#7A9488' }}>
                  {generateMatchReason(userProfile, openUser)}
                </p>
                
                <div className={minimal ? "flex flex-col gap-1 mt-2" : "flex flex-col gap-0 mt-0.5"}>
                  {incomingRequestUserIds.has(openUser.userId) ? (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        console.log('🟢 Opening preview modal for:', openUser.userId);
                        
                        // Fetch the request data to show in preview
                        try {
                          const { collection, query, where, getDocs } = await import('firebase/firestore');
                          const { db } = await import('../firebase');
                          
                          const requestQuery1 = query(
                            collection(db, 'connectionRequests'),
                            where('fromUserId', '==', openUser.userId),
                            where('toUserId', '==', user?.uid),
                            where('status', '==', 'pending')
                          );
                          const requestQuery2 = query(
                            collection(db, 'connectionRequests'),
                            where('senderId', '==', openUser.userId),
                            where('receiverId', '==', user?.uid),
                            where('status', '==', 'pending')
                          );
                          
                          const [snap1, snap2] = await Promise.all([
                            getDocs(requestQuery1),
                            getDocs(requestQuery2)
                          ]);
                          
                          const requestDoc = snap1.docs[0] || snap2.docs[0];
                          if (requestDoc) {
                            const requestData = { id: requestDoc.id, ...requestDoc.data() };
                            console.log('🟢 Request data:', requestData);
                            setPreviewRequestData({
                              ...requestData,
                              fromUser: openUser
                            });
                            setShowRequestPreviewModal(true);
                          } else {
                            console.error('No request found');
                            alert('Kon het verzoek niet vinden.');
                          }
                        } catch (error) {
                          console.error('Error fetching request:', error);
                          alert('Er ging iets mis bij het ophalen van het verzoek.');
                        }
                      }}
                      disabled={connectingUsers.has(openUser.userId)}
                      className="flex-1 px-1 py-0.5 rounded text-[8px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: '#8DA8A0', color: '#ffffff' }}
                    >
                      {connectingUsers.has(openUser.userId) ? 'Bezig...' : 'Bekijk verzoek'}
                    </button>
                    ) : (
                      <>
                        <div className="flex gap-0.5">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!hasPhoneNumber) {
                                alert('Vul eerst je telefoonnummer in bij Instellingen → Profiel om een connectieverzoek te versturen.');
                                return;
                              }
                              handleConnect(openUser);
                            }}
                            disabled={connectingUsers.has(openUser.userId) || pendingUserIds.has(openUser.userId) || !hasPhoneNumber}
                            title={!hasPhoneNumber ? 'Vul eerst je telefoonnummer in bij Instellingen' : ''}
                            className={minimal ? "flex-1 inline-flex items-center justify-center gap-1 rounded-full px-3 py-1.5 text-[11px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed" : "flex-1 inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[8px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"}
                            style={
                              pendingUserIds.has(openUser.userId)
                                ? { backgroundColor: '#F4F4F1', color: '#5E6462' }
                                : !hasPhoneNumber || connectingUsers.has(openUser.userId)
                                ? { backgroundColor: '#E9E7E2', color: '#9B9B9B' }
                                : { backgroundColor: '#8DA8A0', color: '#ffffff' }
                            }
                          >
                            {!pendingUserIds.has(openUser.userId) && !connectingUsers.has(openUser.userId) && hasPhoneNumber && (
                              <Handshake size={9} weight="regular" />
                            )}
                            {pendingUserIds.has(openUser.userId)
                              ? 'Verzonden'
                              : !hasPhoneNumber
                              ? 'Telefoon vereist'
                              : connectingUsers.has(openUser.userId)
                              ? 'Bezig...'
                              : 'Kennismaken'}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedUserForIntroduction({
                                userId: openUser.userId,
                                userName: openUser.userName,
                                userPhoto: openUser.userPhoto
                              });
                              setShowIntroductionModal(true);
                            }}
                            className={minimal ? "rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-colors flex items-center justify-center" : "px-1.5 py-0.5 rounded text-[8px] font-medium transition-colors flex items-center justify-center border"}
                            style={{ backgroundColor: '#FFFFFF', color: '#4F6660', borderColor: '#E9E7E2' }}
                            title="Stel deze persoon voor aan iemand"
                          >
                            Voorstellen
                          </button>
                        </div>
                        {!pendingUserIds.has(openUser.userId) && hasPhoneNumber && !connectingUsers.has(openUser.userId) && (
                          <p className={minimal ? "text-[10px] leading-snug mt-1" : "text-[7px] leading-tight mt-0.5"} style={{ color: '#ABABAB' }}>
                            Je stuurt een uitnodiging. {openUser.userName.split(' ')[0]} beslist zelf.
                          </p>
                        )}
                      </>
                    )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
        </>
      )}
      {!settingsOnly && (minimal ? filteredOpenUsers.length === 0 : userRegions.length > 0 && filteredOpenUsers.length === 0) && (
        <div className={minimal ? "flex min-h-[52vh] flex-col items-center justify-start px-6 pt-24 text-center" : "bg-gray-50 border border-gray-200 rounded-lg p-2 sm:p-2.5 md:p-3 mb-1.5 sm:mb-2 md:mb-4"}>
          {minimal && <UserPlus size={34} weight="regular" className="mb-5 text-[#4F6660]/70" />}
          <p className={minimal ? "text-lg font-semibold leading-tight text-[#2F3A36]" : "text-[11px] sm:text-xs text-gray-700 leading-snug"}>
            {minimal ? 'Geen suggesties gevonden' : 'Geen resultaten voor deze filters.'}
          </p>
          {minimal && (
            <p className="mt-2 max-w-[260px] text-sm leading-relaxed text-[#8A8A84]">Er zijn nu geen ondernemers zichtbaar in jouw netwerkgebied.</p>
          )}
        </div>
      )}

      {/* Unified Accept Popup */}
      {showAcceptPopup && acceptedUser && user && acceptedUser.connectionId ? (
        <ConnectionAcceptedModal
          connectionId={acceptedUser.connectionId}
          otherUserId={acceptedUser.userId}
          otherUsername={acceptedUser.userName}
          otherUserPhoto={acceptedUser.userPhoto}
          currentUserId={user.uid}
          currentUsername={userProfile?.displayName || user.displayName || 'Jij'}
          onClose={() => {
            console.log('🔒 Modal onClose called');
            setShowAcceptPopup(false);
            setAcceptedUser(null);
          }}
          onStartChat={() => {
            console.log('🚀 OpenForConnectionsList onStartChat triggered for:', acceptedUser.connectionId);
            console.log('📦 acceptedUser data:', acceptedUser);
            localStorage.setItem('selectedChatRoomId', acceptedUser.chatRoomId);
            // Set hash FIRST (same as App.tsx)
            console.log('📍 Setting hash to #connecties');
            window.location.hash = '#connecties';
            // Dispatch event
            console.log('📢 Dispatching openConnectionFlowForConnection with mode: chat, connectionId:', acceptedUser.connectionId);
            window.dispatchEvent(new CustomEvent('openConnectionFlowForConnection', {
              detail: { connectionId: acceptedUser.connectionId, mode: 'chat' }
            }));
            // Close popup LAST
            console.log('🔒 Closing popup');
            setShowAcceptPopup(false);
            setAcceptedUser(null);
          }}
          onStartPlanning={async () => {
            console.log('📅 OpenForConnectionsList onStartPlanning triggered');
            setShowAcceptPopup(false);
            setAcceptedUser(null);
            const otherUserId = acceptedUser?.userId;
            const connectionId = acceptedUser?.connectionId || (otherUserId ? await ensureConnectionId(otherUserId) : null);
            if (!connectionId) {
              alert('❌ Kon connectionId niet vinden om agenda te openen.');
              return;
            }
            window.dispatchEvent(new CustomEvent('openConnectionFlowForConnection', {
              detail: { connectionId, mode: 'agenda' }
            }));
            window.location.hash = '#connecties';
          }}
        />
      ) : showAcceptPopup ? (
        <div style={{position: 'fixed', top: 20, right: 20, background: 'transparent', color: 'white', padding: '10px', zIndex: 9999}}>
          DEBUG: Modal NOT rendering! {!acceptedUser ? 'No acceptedUser' : !acceptedUser.connectionId ? 'No connectionId' : !user ? 'No user' : 'Unknown'}
        </div>
      ) : null}

      {/* Request Preview Modal */}
      {showRequestPreviewModal && previewRequestData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className={`bg-white rounded-lg max-w-md w-full p-6 ${previewRequestData.knownContact ? 'border-2 border-[#E0DDD8]' : ''}`}>
            {/* Known Contact Banner */}
            {previewRequestData.knownContact && (
              <div className="mb-4 flex items-center gap-2 px-3 py-2 bg-[rgba(46,95,86,0.08)] border border-[#E0DDD8] rounded-lg">
                <span className="text-lg">👋</span>
                <span className="text-sm font-medium text-[#4F6660]">
                  Deze persoon geeft aan dat jullie elkaar al kennen
                </span>
              </div>
            )}
            
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Connectie verzoek van {previewRequestData.fromUser?.userName || previewRequestData.fromUserName || 'Onbekend'}
            </h3>
            
            {/* Message */}
            {previewRequestData.message && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{previewRequestData.message}</p>
              </div>
            )}
            
            {!previewRequestData.message && (
              <p className="text-sm text-gray-500 mb-4 italic">Geen bericht toegevoegd</p>
            )}
            
            {/* Action Buttons */}
            {previewRequestData.knownContact && previewRequestData.knownContact.status === 'pending' ? (
              <div className="space-y-2">
                <p className="text-sm text-gray-600 mb-3">Kies hoe je verder wilt:</p>
                <button
                  onClick={async () => {
                    try {
                      setProcessingRequest(true);
                      const { connectionRequestService } = await import('../services/connectionRequestService');
                      await connectionRequestService.handleKnownContactResponse(previewRequestData.id, 'direct_connect');
                      
                      const { addDoc, collection } = await import('firebase/firestore');
                      const userIds = [user?.uid, previewRequestData.fromUserId].sort();
                      const connectionRef = await addDoc(collection(db, 'connections'), {
                        user1Id: userIds[0],
                        user2Id: userIds[1],
                        status: 'accepted',
                        createdAt: new Date(),
                        acceptedAt: new Date(),
                        knownContactDirectConnect: true
                      });
                      
                      setShowRequestPreviewModal(false);
                      setPreviewRequestData(null);
                      alert('✅ Direct verbonden! Je kunt nu direct chatten.');
                      window.location.hash = '#connecties';
                    } catch (error) {
                      console.error('Error:', error);
                      alert('Er ging iets mis.');
                    } finally {
                      setProcessingRequest(false);
                    }
                  }}
                  disabled={processingRequest}
                  className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  ✓ Direct Connecten
                </button>
                <button
                  onClick={async () => {
                    try {
                      setProcessingRequest(true);
                      const { connectionRequestService } = await import('../services/connectionRequestService');
                      await connectionRequestService.handleKnownContactResponse(previewRequestData.id, 'verify_call');
                      
                      const { addDoc, collection, updateDoc, doc } = await import('firebase/firestore');
                      const userIds = [user?.uid, previewRequestData.fromUserId].sort();
                      const connectionRef = await addDoc(collection(db, 'connections'), {
                        user1Id: userIds[0],
                        user2Id: userIds[1],
                        status: 'accepted',
                        createdAt: new Date(),
                        acceptedAt: new Date(),
                        knownContact: previewRequestData.knownContact,
                        knownContactVerificationOnly: true
                      });
                      
                      await updateDoc(doc(db, 'connectionRequests', previewRequestData.id), {
                        status: 'accepted',
                        updatedAt: new Date()
                      });
                      
                      setShowRequestPreviewModal(false);
                      setPreviewRequestData(null);
                      alert('✅ Connectie aangemaakt! Je kunt nu direct bellen om te verifiëren.');
                      window.location.hash = '#connecties';
                    } catch (error) {
                      console.error('Error:', error);
                      alert('Er ging iets mis.');
                    } finally {
                      setProcessingRequest(false);
                    }
                  }}
                  disabled={processingRequest}
                  className="w-full bg-[#2E5F56] hover:bg-[#265049] text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  📞 Verificatie bellen
                </button>
                <button
                  onClick={async () => {
                    try {
                      setProcessingRequest(true);
                      const { connectionRequestService } = await import('../services/connectionRequestService');
                      await connectionRequestService.handleKnownContactResponse(previewRequestData.id, 'declined');
                      
                      const { updateDoc, doc } = await import('firebase/firestore');
                      await updateDoc(doc(db, 'connectionRequests', previewRequestData.id), {
                        status: 'declined',
                        updatedAt: new Date()
                      });
                      
                      setShowRequestPreviewModal(false);
                      setPreviewRequestData(null);
                      alert('Verzoek afgewezen.');
                    } catch (error) {
                      console.error('Error:', error);
                      alert('Er ging iets mis.');
                    } finally {
                      setProcessingRequest(false);
                    }
                  }}
                  disabled={processingRequest}
                  className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  ✗ Afwijzen
                </button>
                <button
                  onClick={async () => {
                    try {
                      setProcessingRequest(true);
                      const { connectionRequestService } = await import('../services/connectionRequestService');
                      await connectionRequestService.handleKnownContactResponse(previewRequestData.id, 'continue');
                      setShowRequestPreviewModal(false);
                      setPreviewRequestData(null);
                      // Let normal flow handle it
                      handleAcceptIncomingRequest(previewRequestData.fromUserId);
                    } catch (error) {
                      console.error('Error:', error);
                      alert('Er ging iets mis.');
                    } finally {
                      setProcessingRequest(false);
                    }
                  }}
                  disabled={processingRequest}
                  className="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  → Normale Flow
                </button>
              </div>
            ) : (
              <div className="flex space-x-3">
                <button
                  onClick={async () => {
                    setShowRequestPreviewModal(false);
                    setPreviewRequestData(null);
                    handleAcceptIncomingRequest(previewRequestData.fromUserId);
                  }}
                  disabled={processingRequest}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  Accepteren
                </button>
                <button
                  onClick={async () => {
                    try {
                      setProcessingRequest(true);
                      const { updateDoc, doc } = await import('firebase/firestore');
                      await updateDoc(doc(db, 'connectionRequests', previewRequestData.id), {
                        status: 'declined',
                        updatedAt: new Date()
                      });
                      setShowRequestPreviewModal(false);
                      setPreviewRequestData(null);
                      alert('Verzoek afgewezen.');
                    } catch (error) {
                      console.error('Error:', error);
                      alert('Er ging iets mis.');
                    } finally {
                      setProcessingRequest(false);
                    }
                  }}
                  disabled={processingRequest}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  Weigeren
                </button>
              </div>
            )}
            
            <button
              onClick={() => {
                setShowRequestPreviewModal(false);
                setPreviewRequestData(null);
              }}
              className="w-full mt-3 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Sluiten
            </button>
          </div>
        </div>
      )}

      {/* Introduction Modal */}
      {showIntroductionModal && selectedUserForIntroduction && (
        <IntroductionModal
          isOpen={showIntroductionModal}
          onClose={() => {
            setShowIntroductionModal(false);
            setSelectedUserForIntroduction(null);
          }}
          recommendedUserId={selectedUserForIntroduction.userId}
          recommendedUserName={selectedUserForIntroduction.userName}
          recommendedUserPhoto={selectedUserForIntroduction.userPhoto}
        />
      )}
    </div>
  );
};

export default OpenForConnectionsList;


